import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements, PaymentElement, AddressElement } from '@stripe/react-stripe-js';
import { Button, Loading } from 'react-daisyui';
import { supabase } from '../../../lib/supabase';
import { useAuth } from "../../../modules/authentication/AuthProvider";
import { useTranslation } from 'react-i18next';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

interface PriceDetails {
  amount: number;
  currency: string;
  interval: string;
}

interface CouponDetails {
  id: string;
  name: string;
  amountOff?: number;
  percentOff?: number;
  duration: 'forever' | 'once' | 'repeating';
  durationInMonths?: number;
}

const CheckoutForm: React.FC<{ priceDetails: PriceDetails, couponDetails: CouponDetails | null }> = ({ priceDetails, couponDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/app/getting-started#page=subscription`,
      },
    });

    if (error) {
      console.error('Payment error:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Button type="submit" disabled={!stripe} color="primary" className="mt-4">
        {t('profilePages.stripeCheckout.purchaseButton', {
          price: (priceDetails.amount / 100).toFixed(2),
          currency: priceDetails.currency.toUpperCase()
        })}
      </Button>
    </form>
  );
};

const LoadingSpinner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center items-center h-40">
      <Loading size="lg" />
      <span className="ml-2">{t('profilePages.stripeCheckout.loading')}</span>
    </div>
  );
};

const StripeCheckout: React.FC<{ couponCode: string; planType: string; onSuccess: () => void }> = ({ couponCode, planType, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [priceDetails, setPriceDetails] = useState<PriceDetails | null>(null);
  const [couponDetails, setCouponDetails] = useState<CouponDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPayed, setHasPayed] = useState(false);
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const formatCouponDuration = (duration: string, durationInMonths?: number) => {
    switch (duration) {
      case 'forever':
        return t('profilePages.stripeCheckout.couponDuration.forever');
      case 'once':
        return t('profilePages.stripeCheckout.couponDuration.once');
      case 'repeating':
        return t('profilePages.stripeCheckout.couponDuration.repeating', { months: durationInMonths });
      default:
        return t('profilePages.stripeCheckout.couponDuration.unknown');
    }
  };

  const calculateEffectivePrice = (originalAmount: number, coupon: CouponDetails | null): number => {
    if (!coupon) return originalAmount;
    if (coupon.amountOff) {
      return Math.max(0, originalAmount - coupon.amountOff);
    }
    if (coupon.percentOff) {
      return originalAmount * (1 - coupon.percentOff / 100);
    }
    return originalAmount;
  };

  const handleCreateSubscription = async () => {
    setError(null);
    
    const { data, error } = await supabase.functions.invoke('create-subscription', {
      body: JSON.stringify({
        planType,
        userId: currentUser?.id,
        couponCode,
      }),
    });

    if (error) {
      setError(error);
    } else if (data.hasPayed) {
      setHasPayed(true);
      onSuccess();
    } else {
      setClientSecret(data.clientSecret);
      setPriceDetails(data.priceDetails);
      setCouponDetails(data.couponDetails);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleCreateSubscription();
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-error">{error}</div>;
  }

  if (hasPayed) {
    return (
      <div className="text-center p-5">
        <h2 className="text-2xl font-bold mb-2">{t('profilePages.stripeCheckout.thankYou')}</h2>
        <p>{t('profilePages.stripeCheckout.enjoyService')}</p>
      </div>
    );
  }

  if (clientSecret && priceDetails) {
    const options = {
      clientSecret,
      appearance: {/* You can customize the appearance here */},
    };

    const effectivePrice = calculateEffectivePrice(priceDetails.amount, couponDetails);

    return (
      <Elements stripe={stripePromise} options={options}>
        <div className="max-w-lg mx-auto p-5">
          <AddressElement options={{ mode: 'billing' }} />
          <CheckoutForm priceDetails={{ ...priceDetails, amount: effectivePrice }} couponDetails={couponDetails} />
        </div>
      </Elements>
    );
  }

  return null;
};

export default StripeCheckout;